import { Dialog, Transition } from "@headlessui/react";
import { Address } from "@helium/crypto";
import { XIcon } from "@heroicons/react/outline";
import { Field, FormikHelpers, FormikProps } from "formik";
import { filter, find, get, head, isEmpty, pick } from "lodash";
import Tooltip from "rc-tooltip";
import React, { Fragment, useCallback, useMemo, useRef, useState } from "react";
import * as Yup from "yup";
import shallow from "zustand/shallow";
import Copy from "../../../components/Copy";
import Checkbox from "../../../components/forms/Checkbox";
import DropdownSelect, {
  DropdownSelectOption
} from "../../../components/forms/DropdownSelect";
import FormProvider from "../../../components/forms/FormProvider";
import HandleFieldChange from "../../../components/forms/HandleFieldChange";
import Input from "../../../components/forms/Input";
import InfoIcon from "../../../components/icons/InfoIcon";
import { useContacts } from "../../../context/contacts";
import { PaymentMethod, PaymentTypeKey } from "../../../utils/types";
import useCurrency, { CurrencyKey } from "../../../utils/useCurrency";

interface Props {
  config: EditPaymentMethodConfig;
  setConfig: React.Dispatch<React.SetStateAction<EditPaymentMethodConfig>>;
}

const PaymentMethodFormSidebar: React.FC<Props> = ({ config, setConfig }) => {
  const { contacts, updatePaymentMethod } = useContacts();
  const [currencyOptions, getCurrencyTitle] = useCurrency(
    (state) => [state.currencyOptions, state.getCurrencyTitle],
    shallow
  );
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const dialogRef = useRef(null);

  const closeSidebar = useCallback(
    (paymentMethod?: any) => {
      setConfig({ show: false, paymentMethod, initialCurrency: undefined });
      setErrorMessage(null);
    },
    [setConfig]
  );

  const initialValues = useMemo(() => {
    if (!config?.paymentMethod) {
      const currency = config.initialCurrency || CurrencyKey.hnt;
      const paymentTypeOption = head(paymentTypesByCurrency[currency]);
      return {
        name: paymentTypeOption?.title,
        currency: find(currencyOptions, { key: currency }),
        paymentType: paymentTypeOption,
        primary: true
      };
    }

    const { currency, type, name, metadata, primary } = config?.paymentMethod;

    const parsedMetadata = JSON.parse(metadata || "{}");

    return {
      name,
      currency: find(currencyOptions, { key: currency }),
      paymentType: find(paymentTypesByCurrency[currency], {
        key: type
      }),
      primary,
      ...parsedMetadata
    };
  }, [config.initialCurrency, config?.paymentMethod, currencyOptions]);

  const onSubmit = useCallback(
    async (
      formValues: FormValues,
      formikHelpers: FormikHelpers<FormValues>
    ) => {
      const { name, paymentType, currency, primary } = formValues;

      setErrorMessage(null);

      let linkedPaymentMethods = config.linkedPaymentMethods || [];

      if (config.contactLinkedUuid && isEmpty(linkedPaymentMethods)) {
        linkedPaymentMethods =
          contacts[config.contactLinkedUuid].payment_methods;
      }

      const filteredDuplicatedPaymentMethods = filter(
        linkedPaymentMethods,
        (item) =>
          item.type === PaymentTypeKey.walletAddress &&
          JSON.parse(item.metadata)[PaymentTypeKey.walletAddress] ===
            formValues.walletAddress &&
          config.paymentMethod?.uuid !== item.uuid
      );

      if (filteredDuplicatedPaymentMethods.length > 0) {
        setErrorMessage(
          "You already have a payment method with this wallet address"
        );

        return;
      }

      const paymentMethodToSave: PaymentMethod = {
        uuid: config.paymentMethod?.uuid || null,
        name,
        type: paymentType.key,
        currency: currency.key,
        metadata: JSON.stringify(
          pick(
            formValues,
            find(paymentTypesByCurrency[currency.key], {
              key: paymentType.key
            })?.fields || []
          )
        ),
        primary
      };

      if (config.paymentMethod?.uuid && config.contactLinkedUuid) {
        const apiResponse = await updatePaymentMethod(
          config.contactLinkedUuid,
          config.paymentMethod.uuid,
          paymentMethodToSave
        );

        if (apiResponse.status) {
          config?.onSuccess && config?.onSuccess(paymentMethodToSave);
          return closeSidebar();
        } else {
          formikHelpers.setSubmitting(false);
          return setErrorMessage("Something went wrong");
        }
      }

      if (config?.onSuccess) {
        await config?.onSuccess(paymentMethodToSave);
      }

      closeSidebar();

      formikHelpers.setSubmitting(false);
    },
    [closeSidebar, config, contacts, updatePaymentMethod]
  );

  return (
    <Transition.Root show={config.show} as={Fragment}>
      <Dialog
        as="div"
        static
        className="fixed inset-0 z-30 overflow-hidden"
        open={config.show}
        onClose={() => {
          closeSidebar();
        }}
        initialFocus={dialogRef}
      >
        <div ref={dialogRef} className="absolute inset-0 overflow-hidden">
          <Dialog.Overlay className="absolute inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          <div className="fixed inset-y-0 pl-16 max-w-full right-0 flex duration-500">
            <div className="absolute inset-0" />
            <Transition.Child
              as={Fragment}
              enter="transform transition ease-in-out duration-500 sm:duration-700"
              enterFrom="translate-x-full"
              enterTo="translate-x-0"
              leave="transform transition ease-in-out duration-500 sm:duration-700"
              leaveFrom="translate-x-0"
              leaveTo="translate-x-full"
            >
              <div className="relative w-screen max-w-full sm:max-w-md">
                <FormProvider
                  classnames={{ wrapper: "xl:max-w-7xl h-full" }}
                  onCancel={() => closeSidebar()}
                  error={errorMessage}
                  formikProps={{
                    onSubmit,
                    initialValues,
                    validationSchema: Yup.object().shape({
                      name: Yup.string().required("Required"),
                      currency: Yup.object().required("Required"),
                      paymentType: Yup.object().required("Required"),
                      walletAddress: Yup.string().nullable(),
                      email: Yup.string()
                        .nullable()
                        .email("Please enter a valid email address"),
                      handle: Yup.string().nullable(),
                      iban: Yup.string().nullable(),
                      bic: Yup.string().nullable(),
                      otherField: Yup.string().nullable(),
                      otherValue: Yup.string().nullable(),
                      primary: Yup.boolean()
                    })
                  }}
                  orderedFieldNames={ORDERED_FIELD_NAMES}
                >
                  {({
                    isSubmitting,
                    setFieldValue,
                    values: formValues
                  }: FormikProps<FormValues>) => {
                    return (
                      <div className="flex-1 h-0 overflow-y-auto">
                        <div className="py-6 px-4 bg-hotspotty-700 sm:px-6 dark:bg-gray-800">
                          <div className="flex items-center justify-between">
                            <Dialog.Title className="flex items-center text-lg font-medium text-white">
                              {config?.paymentMethod &&
                              config.paymentMethod.uuid
                                ? "Edit"
                                : "New"}{" "}
                              Payment Method
                            </Dialog.Title>
                            <div className="ml-3 h-7 flex items-center">
                              <button
                                autoFocus
                                type="button"
                                className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-hotspotty-400 dark:bg-gray-800 dark:hover:text-white"
                                onClick={() => closeSidebar()}
                              >
                                <span className="sr-only">Close panel</span>
                                <XIcon className="h-6 w-6" aria-hidden="true" />
                              </button>
                            </div>
                          </div>
                        </div>

                        <div className="flex-1 flex flex-col justify-between">
                          <div className="px-4 divide-y divide-gray-200 dark:divide-gray-500 sm:px-6">
                            <div className="space-y-6 pt-6 pb-5">
                              <HandleFieldChange<FormValues>
                                name="walletAddress"
                                onChange={() => setErrorMessage(null)}
                              />

                              <Field
                                id="name"
                                name="name"
                                label="Name"
                                placeholder="My principal payment"
                                type="text"
                                component={Input}
                                disabled={isSubmitting}
                              />

                              <Field
                                className="-mt-1"
                                name="currency"
                                label="Currency"
                                disabled={
                                  isSubmitting || config.paymentMethod?.uuid
                                }
                                items={filter(
                                  currencyOptions,
                                  (currencyOption) =>
                                    !isEmpty(
                                      paymentTypesByCurrency[
                                        currencyOption.key as CurrencyKey
                                      ]
                                    )
                                )}
                                itemToString={(
                                  currencyOption: DropdownSelectOption<CurrencyKey>
                                ) => getCurrencyTitle(currencyOption.key)}
                                optionToString={(
                                  currencyOption: DropdownSelectOption<CurrencyKey>
                                ) => getCurrencyTitle(currencyOption.key)}
                                component={DropdownSelect}
                              />

                              <HandleFieldChange<FormValues>
                                name="currency"
                                onChange={(
                                  currencyOption: DropdownSelectOption<CurrencyKey>
                                ) => {
                                  setFieldValue(
                                    "paymentType",
                                    head(
                                      paymentTypesByCurrency[
                                        currencyOption.key as CurrencyKey
                                      ]
                                    )
                                  );

                                  setFieldValue("walletAddress", "");
                                  setFieldValue("email", "");
                                  setFieldValue("handle", "");
                                  setFieldValue("iban", "");
                                  setFieldValue("bic", "");
                                  setFieldValue("otherField", "");
                                  setFieldValue("otherValue", "");
                                }}
                              />

                              <Field
                                className="-mt-1"
                                name="paymentType"
                                label="Payment type"
                                disabled={
                                  isSubmitting || config.paymentMethod?.uuid
                                }
                                items={
                                  paymentTypesByCurrency[
                                    formValues.currency.key
                                  ]
                                }
                                itemToString={(
                                  paymentTypeOption: DropdownSelectOption<PaymentTypeKey>
                                ) => paymentTypeOption.title}
                                optionToString={(
                                  paymentTypeOption: DropdownSelectOption<PaymentTypeKey>
                                ) => paymentTypeOption.title}
                                component={DropdownSelect}
                              />

                              <HandleFieldChange<FormValues>
                                name="paymentType"
                                onChange={(
                                  paymentTypeOption: DropdownSelectOption<PaymentTypeKey>
                                ) => {
                                  setFieldValue(
                                    "name",
                                    paymentTypeOption.title
                                  );

                                  if (
                                    paymentTypeOption.key ===
                                    PaymentTypeKey.wiseByEmail
                                  ) {
                                    const email = get(contacts, [
                                      config.contactLinkedUuid as string,
                                      "email"
                                    ]);
                                    if (email) setFieldValue("email", email);
                                  }
                                }}
                              />

                              {renderExtraPaymentTypeFields(
                                formValues,
                                isSubmitting || !!config.paymentMethod?.uuid
                              )}

                              <Field
                                label={
                                  <div className="inline-flex items-center">
                                    <div>
                                      Use as default payment method for{" "}
                                      {getCurrencyTitle(
                                        formValues.currency.key
                                      )}
                                    </div>
                                    <Tooltip
                                      placement="top"
                                      overlay="The commission reports with payment instructions will be generated based on the contact's default payment method for the commission report's currency."
                                    >
                                      <InfoIcon className="inline-flex h-4 w-4 ml-1" />
                                    </Tooltip>
                                  </div>
                                }
                                name="primary"
                                disabled={
                                  isSubmitting || config.paymentMethod?.primary
                                }
                                type="checkbox"
                                component={Checkbox}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  }}
                </FormProvider>
              </div>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default PaymentMethodFormSidebar;

//
// Utils
//

interface FormValues {
  name: string;
  currency: DropdownSelectOption<CurrencyKey>;
  paymentType: DropdownSelectOption<PaymentTypeKey>;
  walletAddress?: string;
  email?: string;
  handle?: string;
  iban?: string;
  bic?: string;
  otherField?: string;
  otherValue?: string;
  primary: boolean;
}

const ORDERED_FIELD_NAMES = [
  "name",
  "currency",
  "paymentType",
  "walletAddress",
  "email",
  "handle",
  "iban",
  "bic",
  "otherField",
  "otherValue",
  "primary"
];

export interface EditPaymentMethodConfig {
  show: boolean;
  parentForm?: string;
  contactLinkedUuid?: string;
  paymentMethod?: PaymentMethod | undefined;
  linkedPaymentMethods?: PaymentMethod[];
  onSuccess?: (payment: PaymentMethod) => Promise<void>;
  initialCurrency?: CurrencyKey | undefined;
}

interface EnhancedPaymentTypeOption
  extends DropdownSelectOption<PaymentTypeKey> {
  fields: string[];
}

export const enhancedPaymentMethods: {
  [key in keyof typeof PaymentTypeKey]: EnhancedPaymentTypeOption;
} = {
  [PaymentTypeKey.walletAddress]: {
    key: PaymentTypeKey.walletAddress,
    title: "Wallet address",
    fields: ["walletAddress"]
  },
  [PaymentTypeKey.paypal]: {
    key: PaymentTypeKey.paypal,
    title: "PayPal",
    fields: ["email"]
  },
  [PaymentTypeKey.iban]: {
    key: PaymentTypeKey.iban,
    title: "IBAN",
    fields: ["iban", "bic"]
  },
  [PaymentTypeKey.venmo]: {
    key: PaymentTypeKey.venmo,
    title: "Venmo",
    fields: ["handle"]
  },
  [PaymentTypeKey.cashApp]: {
    key: PaymentTypeKey.cashApp,
    title: "Cash App",
    fields: ["handle"]
  },
  [PaymentTypeKey.cash]: {
    key: PaymentTypeKey.cash,
    title: "Cash",
    fields: []
  },
  [PaymentTypeKey.wiseByEmail]: {
    key: PaymentTypeKey.wiseByEmail,
    title: "Wise by email",
    fields: ["email"]
  },
  [PaymentTypeKey.other]: {
    key: PaymentTypeKey.other,
    title: "Other",
    fields: ["otherField", "otherValue"]
  }
};

const defaultFiatPaymentMethods = [
  enhancedPaymentMethods.paypal,
  enhancedPaymentMethods.iban,
  enhancedPaymentMethods.cash,
  enhancedPaymentMethods.other
];

const defaultFiatPaymentMethodsWithWise = [
  enhancedPaymentMethods.wiseByEmail,
  ...defaultFiatPaymentMethods
];

export const paymentTypesByCurrency: {
  [currencyKey in keyof typeof CurrencyKey]: EnhancedPaymentTypeOption[];
} = {
  [CurrencyKey.hnt]: [enhancedPaymentMethods.walletAddress],
  [CurrencyKey.usd]: [
    enhancedPaymentMethods.cashApp,
    enhancedPaymentMethods.venmo,
    ...defaultFiatPaymentMethodsWithWise
  ],
  [CurrencyKey.eur]: defaultFiatPaymentMethodsWithWise,
  [CurrencyKey.gbp]: defaultFiatPaymentMethodsWithWise,
  [CurrencyKey.cad]: defaultFiatPaymentMethodsWithWise,
  [CurrencyKey.aud]: defaultFiatPaymentMethodsWithWise,
  [CurrencyKey.brl]: defaultFiatPaymentMethodsWithWise,
  [CurrencyKey.jpy]: defaultFiatPaymentMethodsWithWise,
  [CurrencyKey.dkk]: defaultFiatPaymentMethodsWithWise,
  [CurrencyKey.sek]: defaultFiatPaymentMethodsWithWise,
  [CurrencyKey.chf]: defaultFiatPaymentMethodsWithWise,
  [CurrencyKey.inr]: defaultFiatPaymentMethodsWithWise,
  [CurrencyKey.cny]: defaultFiatPaymentMethods,
  [CurrencyKey.myr]: defaultFiatPaymentMethodsWithWise,
  [CurrencyKey.sgd]: defaultFiatPaymentMethodsWithWise,
  [CurrencyKey.nzd]: defaultFiatPaymentMethodsWithWise,
  [CurrencyKey.btc]: [enhancedPaymentMethods.walletAddress],
  [CurrencyKey.eth]: [enhancedPaymentMethods.walletAddress]
};

const renderExtraPaymentTypeFields = (
  formValues: FormValues,
  disabled: boolean
) => {
  switch (formValues.paymentType.key) {
    case PaymentTypeKey.walletAddress:
      return (
        <Field
          name="walletAddress"
          label={
            <div className="flex items-center">
              {formValues.currency.title} wallet address
              {disabled && formValues.walletAddress && (
                <Copy
                  text={formValues.walletAddress}
                  label={`${formValues.currency.title} wallet address`}
                  className="inline ml-1 h-4 w-4"
                />
              )}
            </div>
          }
          placeholder="14496bk...6EXZon"
          type="text"
          component={Input}
          disabled={disabled}
          validate={(walletAddress: string) => {
            if (!walletAddress) return "Required";

            if (
              formValues.currency.key === CurrencyKey.hnt &&
              !Address.isValid(walletAddress)
            ) {
              return `Please provide a valid HNT wallet address`;
            }
          }}
        />
      );
    case PaymentTypeKey.paypal:
      return (
        <Field
          name="email"
          label="Email address"
          placeholder="john@doe.com"
          type="text"
          component={Input}
          disabled={disabled}
        />
      );
    case PaymentTypeKey.cashApp:
      return (
        <Field
          name="handle"
          label="Cash App handle"
          placeholder="$john"
          type="text"
          component={Input}
          disabled={disabled}
          validate={(handle: string) => {
            if (!handle) return "Required";
            if (!handle.startsWith("$")) {
              return "Your handle needs to start with a $ sign";
            }
            if (handle.length < 3) {
              return "Your handle is too short";
            }
          }}
        />
      );
    case PaymentTypeKey.venmo:
      return (
        <Field
          name="handle"
          label="Venmo handle"
          placeholder="@john"
          type="text"
          component={Input}
          disabled={disabled}
          validate={(handle: string) => {
            if (!handle) return "Required";
            if (!handle.startsWith("@")) {
              return "Your handle needs to start with a @ sign";
            }
            if (handle.length < 3) {
              return "Your handle is too short";
            }
          }}
        />
      );
    case PaymentTypeKey.wiseByEmail:
      return (
        <Field
          name="email"
          label="Email address"
          placeholder="john@doe.com"
          type="text"
          component={Input}
          disabled={disabled}
        />
      );
    case PaymentTypeKey.other:
      return (
        <>
          <Field
            name="otherField"
            label="Field"
            type="text"
            component={Input}
            disabled={disabled}
            validate={(field: string) => {
              if (!field) return "Required";
            }}
          />
          <Field
            name="otherValue"
            label="Value"
            type="text"
            textarea
            rows={3}
            component={Input}
            disabled={disabled}
            validate={(value: string) => {
              if (!value) return "Required";
            }}
          />
        </>
      );
    case PaymentTypeKey.iban:
      return (
        <>
          <Field
            id="iban"
            name="iban"
            label="IBAN"
            placeholder="XX000000000000000"
            type="text"
            component={Input}
            disabled={disabled}
            validate={() => {
              if (!formValues.iban) {
                return "Please enter a valid IBAN";
              }

              return null;
            }}
          />
          <Field
            id="bic"
            name="bic"
            label="BIC"
            placeholder="XXXXX"
            type="text"
            component={Input}
            disabled={disabled}
          />
        </>
      );
    default:
      return null;
  }
};
